/**
 * CalendarHeader Component
 * Navigation controls and view switcher for GoldenWings calendar
 */

import React from 'react';
import {
  Button,
  Select,
  Space,
  DatePicker,
  Badge,
  Tooltip,
  Checkbox,
} from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  FilterOutlined,
  SunOutlined,
  MoonOutlined,
  CloudOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { Resource } from '@/api/types/calendars.types';
import './CalendarHeader.css';

export type CalendarView =
  | '1day' // 1 day vertical (today only)
  | '2day' // 2 days vertical (today + tomorrow)
  | '3day' // 3 days vertical (yesterday + today + tomorrow)
  | '7day' // 7 days vertical (yesterday + 6 days forward)
  | 'week-new' // Week vertical (Monday-based)
  | 'day-h-1h' // Horizontal 1 day, 1h resolution
  | 'day-h-2d' // Horizontal 2 days, 1h resolution
  | 'day-h-3d' // Horizontal 3 days, 2h resolution
  | 'week-h' // Horizontal 7 days, 4h resolution
  | 'month' // Month view
  | 'year' // Year view - standard 4x3 month grid
  | 'year-agenda'; // Year agenda view - list of events by day

interface CalendarHeaderProps {
  /** Current view mode */
  view: CalendarView;
  /** Current date */
  currentDate: Date;
  /** Callback when view changes */
  onViewChange: (view: CalendarView) => void;
  /** Callback when date changes */
  onDateChange: (date: Date) => void;
  /** Callback to navigate to previous period */
  onPrevious: () => void;
  /** Callback to navigate to next period */
  onNext: () => void;
  /** Callback to jump to today */
  onToday: () => void;
  /** Whether filters are visible */
  filtersVisible?: boolean;
  /** Callback to toggle filters */
  onToggleFilters?: () => void;
  /** Number of active filters */
  activeFiltersCount?: number;
  /** Selected resource IDs for filter tooltip */
  selectedResourceIds?: string[];
  /** All available resources */
  allResources?: Resource[];
  /** Whether to show sunrise/sunset overlay */
  showDayNight?: boolean;
  /** Callback when show/hide sunrise/sunset changes */
  onShowDayNightChange?: (show: boolean) => void;
  /** Whether day/night toggle is disabled */
  dayNightDisabled?: boolean;
  /** Reason why day/night is disabled (for tooltip) */
  dayNightDisabledReason?: string;
  /** Whether to show weather forecast */
  showWeather?: boolean;
  /** Callback when show/hide weather changes */
  onShowWeatherChange?: (show: boolean) => void;
  /** Whether weather toggle is disabled */
  weatherDisabled?: boolean;
  /** Reason why weather is disabled (for tooltip) */
  weatherDisabledReason?: string;
}

/**
 * Format the period display based on current view
 */
const formatPeriod = (date: Date, view: CalendarView): string => {
  const d = dayjs(date);

  switch (view) {
    case '1day':
      return d.format('dddd, MMMM D, YYYY');
    case '2day': {
      const end = d.add(1, 'day');
      if (d.month() === end.month()) {
        return `${d.format('D')}-${end.format('D')} ${d.format('MMMM YYYY')}`;
      }
      return `${d.format('D MMM')} - ${end.format('D MMM YYYY')}`;
    }
    case '3day': {
      const start = d.subtract(1, 'day');
      const end = d.add(1, 'day');
      if (start.month() === end.month()) {
        return `${start.format('D')}-${end.format('D')} ${start.format('MMMM YYYY')}`;
      }
      return `${start.format('D MMM')} - ${end.format('D MMM YYYY')}`;
    }
    case '7day': {
      const start = d.subtract(1, 'day');
      const end = start.add(6, 'day');
      if (start.month() === end.month()) {
        return `${start.format('D')}-${end.format('D')} ${start.format('MMMM YYYY')}`;
      }
      return `${start.format('D MMM')} - ${end.format('D MMM YYYY')}`;
    }
    case 'week-new': {
      // Calculate Monday of current week
      const dayOfWeek = d.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      const mondayBased = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const startOfWeek = d.subtract(mondayBased, 'day');
      const endOfWeek = startOfWeek.add(6, 'day');
      if (startOfWeek.month() === endOfWeek.month()) {
        return `${startOfWeek.format('D')}-${endOfWeek.format('D')} ${startOfWeek.format('MMMM YYYY')}`;
      }
      return `${startOfWeek.format('D MMM')} - ${endOfWeek.format('D MMM YYYY')}`;
    }
    case 'day-h-1h':
      return d.format('dddd, D. MMMM YYYY');
    case 'day-h-2d': {
      const end = d.add(1, 'day');
      if (d.month() === end.month()) {
        return `${d.format('D')}-${end.format('D')} ${d.format('MMMM YYYY')}`;
      }
      return `${d.format('D MMM')} - ${end.format('D MMM YYYY')}`;
    }
    case 'day-h-3d': {
      const end = d.add(2, 'day');
      if (d.month() === end.month()) {
        return `${d.format('D')}-${end.format('D')} ${d.format('MMMM YYYY')}`;
      }
      return `${d.format('D MMM')} - ${end.format('D MMM YYYY')}`;
    }
    case 'week-h': {
      const end = d.add(6, 'day');
      if (d.month() === end.month()) {
        return `${d.format('D')}-${end.format('D')} ${d.format('MMMM YYYY')}`;
      }
      return `${d.format('D MMM')} - ${end.format('D MMM YYYY')}`;
    }
    case 'month':
      return d.format('MMMM YYYY');
    case 'year':
      return d.format('YYYY');
    case 'year-agenda':
      return `${d.format('YYYY')} Agenda`;
    default:
      return d.format('MMMM YYYY');
  }
};

/**
 * Format the daily period with clickable parts
 * Format: "weekday, month day, year"
 * - weekday → click to switch to weekly view
 * - month → click to switch to monthly view
 * - year → click to switch to yearly view
 */
const formatDailyPeriodClickable = (
  date: Date,
  onViewChange: (view: CalendarView) => void
): React.ReactNode => {
  const d = dayjs(date);
  const weekday = d.format('dddd');
  const month = d.format('MMMM');
  const day = d.format('D');
  const year = d.format('YYYY');

  return (
    <>
      <span
        className="calendar-period-clickable"
        onClick={() => onViewChange('week-new')}
        title="Switch to weekly view"
      >
        {weekday}
      </span>
      {', '}
      <span
        className="calendar-period-clickable"
        onClick={() => onViewChange('month')}
        title="Switch to monthly view"
      >
        {month}
      </span>
      {` ${day}, `}
      <span
        className="calendar-period-clickable"
        onClick={() => onViewChange('year')}
        title="Switch to yearly view"
      >
        {year}
      </span>
    </>
  );
};

/**
 * Generate filter summary tooltip content
 * Shows concise summary of selected resources by type
 */
const generateFilterSummary = (
  selectedResourceIds: string[],
  allResources: Resource[]
): React.ReactNode => {
  if (selectedResourceIds.length === 0) {
    return <div style={{ padding: '4px 0' }}>Nema selektovanih filtera</div>;
  }

  // Get selected resources
  const selectedResources = allResources.filter((r) =>
    selectedResourceIds.includes(r.id)
  );

  // Count by resource type
  const counts = {
    AIRPORT: 0,
    AIRPLANE: 0,
    INSTRUCTOR: 0,
    PILOT: 0,
  };

  selectedResources.forEach((resource) => {
    if (counts[resource.resource_type as keyof typeof counts] !== undefined) {
      counts[resource.resource_type as keyof typeof counts]++;
    }
  });

  // Build summary parts
  const parts: string[] = [];
  if (counts.AIRPORT > 0)
    parts.push(`🏢 ${counts.AIRPORT} aerodrom${counts.AIRPORT > 1 ? 'a' : ''}`);
  if (counts.AIRPLANE > 0)
    parts.push(`✈️ ${counts.AIRPLANE} avion${counts.AIRPLANE > 1 ? 'a' : ''}`);
  if (counts.INSTRUCTOR > 0)
    parts.push(
      `👨‍✈️ ${counts.INSTRUCTOR} instruktor${counts.INSTRUCTOR > 1 ? 'a' : ''}`
    );
  if (counts.PILOT > 0)
    parts.push(`🎓 ${counts.PILOT} student${counts.PILOT > 1 ? 'a' : ''}`);

  return (
    <div style={{ padding: '4px 0' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        Aktivni filteri ({selectedResourceIds.length})
      </div>
      {parts.map((part, index) => (
        <div key={index} style={{ fontSize: 12 }}>
          {part}
        </div>
      ))}
    </div>
  );
};

export const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  view,
  currentDate,
  onViewChange,
  onDateChange,
  onPrevious,
  onNext,
  onToday,
  filtersVisible = false,
  onToggleFilters,
  activeFiltersCount = 0,
  selectedResourceIds = [],
  allResources = [],
  showDayNight = false,
  onShowDayNightChange,
  dayNightDisabled = false,
  dayNightDisabledReason = '',
  showWeather = false,
  onShowWeatherChange,
  weatherDisabled = false,
  weatherDisabledReason = '',
}) => {
  // const { t } = useTranslation(); // TODO: Add translations

  const handleDatePickerChange = (date: Dayjs | null) => {
    if (date) {
      onDateChange(date.toDate());
    }
  };

  return (
    <div className="calendar-header">
      <div className="calendar-header-left">
        <Space>
          <Button onClick={onToday} icon={<CalendarOutlined />}>
            Today
          </Button>
          <Button.Group>
            <Button icon={<LeftOutlined />} onClick={onPrevious} />
            <Button icon={<RightOutlined />} onClick={onNext} />
          </Button.Group>
          {onToggleFilters && (
            <Tooltip
              title={generateFilterSummary(selectedResourceIds, allResources)}
              placement="bottom"
              color="rgba(0, 0, 0, 0.85)"
            >
              <Badge count={activeFiltersCount} offset={[-5, 5]}>
                <Button
                  icon={<FilterOutlined />}
                  onClick={onToggleFilters}
                  type={filtersVisible ? 'primary' : 'default'}
                >
                  Filteri
                </Button>
              </Badge>
            </Tooltip>
          )}
        </Space>
      </div>

      <div className="calendar-header-center">
        <h2 className="calendar-period">
          {view === '1day'
            ? formatDailyPeriodClickable(currentDate, onViewChange)
            : formatPeriod(currentDate, view)}
        </h2>
      </div>

      <div className="calendar-header-right">
        <Space>
          {onShowDayNightChange && (
            <Tooltip
              title={
                dayNightDisabled
                  ? dayNightDisabledReason
                  : 'Prikaži izlazak/zalazak sunca'
              }
            >
              <Checkbox
                checked={showDayNight}
                onChange={(e) => onShowDayNightChange(e.target.checked)}
                disabled={dayNightDisabled}
              >
                <SunOutlined style={{ color: '#faad14', marginRight: 4 }} />
                <MoonOutlined style={{ color: '#722ed1' }} />
              </Checkbox>
            </Tooltip>
          )}
          {onShowWeatherChange && (
            <Tooltip
              title={
                weatherDisabled
                  ? weatherDisabledReason
                  : 'Prikaži vremensku prognozu'
              }
            >
              <Checkbox
                checked={showWeather}
                onChange={(e) => onShowWeatherChange(e.target.checked)}
                disabled={weatherDisabled}
              >
                <CloudOutlined style={{ color: '#1890ff' }} />
              </Checkbox>
            </Tooltip>
          )}
          <DatePicker
            value={dayjs(currentDate)}
            onChange={handleDatePickerChange}
            allowClear={false}
            format="D. MMM YYYY"
          />
          <Select
            value={view}
            onChange={onViewChange}
            style={{ width: 160 }}
            listHeight={500}
            options={[
              // Vertical Multi-Day Views
              { value: '1day', label: '1 Day' },
              { value: '2day', label: '2 Days' },
              { value: '3day', label: '3 Days' },
              { value: '7day', label: '7 Days' },
              { value: 'week-new', label: 'Week (Mon-Sun)' },
              { type: 'divider' as const },
              // Horizontal Views
              { value: 'day-h-1h', label: 'Day Horizontal' },
              { value: 'day-h-2d', label: '2 Days Horizontal' },
              { value: 'day-h-3d', label: '3 Days Horizontal' },
              { value: 'week-h', label: '7 Days Horizontal' },
              { type: 'divider' as const },
              // Other Views
              { value: 'month', label: 'Month' },
              { value: 'year', label: 'Year' },
              { value: 'year-agenda', label: 'Year Agenda' },
            ]}
          />
        </Space>
      </div>
    </div>
  );
};
